import { useState, useCallback, useEffect } from "react";
import { AttachmentFile } from "@/components/Attachment/types";
import useAuth from "@/hooks/useAuth";
import { userService } from "@/services/userService";
import { projectService } from "@/services/projectService";
import { clientService } from "@/services/clientService";
import { contactService } from "@/services/contactService";
import { shiftsService } from "@/services/shiftService";
import { invoiceService } from "@/services/invoiceService";
import { estimateService } from "@/services/estimateService";
import { timecardService } from "@/services/timecardService";
import { courseService } from "@/services/courseService";
import { investigationService } from "@/services/investigationService";
import { expenseService } from "@/services/expenseService";
import { EntityType } from "../types";

interface FileMetadata {
  id: string;
  name: string;
  size: number;
  type: string;
  url: string;
  bucket?: string;
  uploadedAt: string;
  uploadedBy: string;
}

// Interface for services that support file operations
interface FileService {
  getFiles(entityId: string): Promise<FileMetadata[]>;
  uploadFile(entityId: string, file: File, uploadedBy: string): Promise<FileMetadata>;
  deleteFile(entityId: string, fileId: string): Promise<boolean>;
  renameFile(entityId: string, fileId: string, newName: string): Promise<FileMetadata>;
}

// Map entity type to bucket name
function getEntityBucket(entityType: EntityType): string {
  switch (entityType) {
    case "user":
      return "users";
    case "project":
      return "projects";
    case "client":
      return "clients";
    case "contact":
      return "contacts";
    case "shift":
      return "shifts";
    case "invoice":
      return "invoices";
    case "investigation":
      return "investigations";
    case "estimate":
      return "estimates";
    case "time_card":
      return "time_cards";
    case "course":
      return "courses";
    case "expense":
      return "expenses";
    default:
      return "projects";
  }
}

// Map entity type to service (cast as FileService since all support file operations)
function getEntityService(entityType: EntityType): FileService {
  switch (entityType) {
    case "user":
      return userService as unknown as FileService;
    case "project":
      return projectService as unknown as FileService;
    case "client":
      return clientService as unknown as FileService;
    case "contact":
      return contactService as unknown as FileService;
    case "shift":
      return shiftsService as unknown as FileService;
    case "invoice":
      return invoiceService as unknown as FileService;
    case "estimate":
      return estimateService as unknown as FileService;
    case "time_card":
      return timecardService as unknown as FileService;
    case "course":
      return courseService as unknown as FileService;
    case "investigation":
      return investigationService as unknown as FileService;
    case "expense":
      return expenseService as unknown as FileService;
    default:
      throw new Error(`Unknown entity type: ${entityType}`);
  }
}

export function useFiles(entityType: EntityType, entityId?: string) {
  const { user } = useAuth();
  const [files, setFiles] = useState<AttachmentFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load files from storage
  const loadFiles = useCallback(async () => {
    if (!entityId) {
      setFiles([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const service = getEntityService(entityType);
      const fileMetadata: FileMetadata[] = await service.getFiles(entityId);
      const bucket = getEntityBucket(entityType);

      // Convert to AttachmentFile format
      const attachmentFiles: AttachmentFile[] = fileMetadata.map((file) => ({
        id: file.id,
        name: file.name,
        size: file.size,
        type: file.type,
        uri: file.url,
        bucket: file.bucket || bucket,
        category: file.type.startsWith("image/")
          ? "image"
          : file.type.startsWith("video/")
            ? "video"
            : file.type.startsWith("audio/")
              ? "audio"
              : file.type.includes("pdf") || file.type.includes("document")
                ? "document"
                : "other",
        uploadStatus: "completed",
      }));

      setFiles(attachmentFiles);
    } catch (err) {
      console.error("Error loading files:", err);
      setError(err instanceof Error ? err.message : "Failed to load files");
    } finally {
      setLoading(false);
    }
  }, [entityId, entityType]);

  // Upload files
  const uploadFiles = useCallback(
    async (newFiles: File[]) => {
      if (!user || !entityId) {
        setError("User not authenticated or no entity selected");
        return;
      }

      if (newFiles.length === 0) return;

      try {
        setLoading(true);
        setError(null);

        const service = getEntityService(entityType);

        for (const file of newFiles) {
          await service.uploadFile(entityId, file, user.id);
        }

        // Reload files from server
        await loadFiles();
      } catch (err) {
        console.error("Error uploading files:", err);
        setError(err instanceof Error ? err.message : "Failed to upload files");
      } finally {
        setLoading(false);
      }
    },
    [user, entityId, entityType, loadFiles],
  );

  // Remove a file
  const removeFile = useCallback(
    async (fileId: string) => {
      if (!entityId) return;

      try {
        const service = getEntityService(entityType);
        await service.deleteFile(entityId, fileId);
        setFiles((prevFiles) => prevFiles.filter((file) => file.id !== fileId));
      } catch (err) {
        console.error("Failed to delete file:", err);
        setError(err instanceof Error ? err.message : "Failed to delete file");
      }
    },
    [entityId, entityType],
  );

  // Rename a file
  const renameFile = useCallback(
    async (fileId: string, newName: string) => {
      if (!entityId) return;

      try {
        setLoading(true);
        setError(null);

        const service = getEntityService(entityType);
        const updatedMetadata = await service.renameFile(entityId, fileId, newName);

        // Update local state
        setFiles((prevFiles) =>
          prevFiles.map((file) => (file.id === fileId ? { ...file, name: updatedMetadata.name } : file)),
        );
      } catch (err) {
        console.error("Failed to rename file:", err);
        setError(err instanceof Error ? err.message : "Failed to rename file");
      } finally {
        setLoading(false);
      }
    },
    [entityId, entityType],
  );

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Auto-load files on mount and when entityId/entityType changes
  useEffect(() => {
    if (entityId) {
      loadFiles();
    }
  }, [entityId, loadFiles]);

  return {
    files,
    loading,
    error,
    uploadFiles,
    removeFile,
    renameFile,
    clearError,
    reload: loadFiles,
  };
}
